<template>
    <div
        :class="{ 'live-mode-menubar': isLiveMode }"
        :style="{ '--nav-inset': navInsetPx }"
        class="menubar-wrapper"
    >
        <MenuBar ref="menubarRef" :class="{ 'live-mode': isLiveMode }">
            <template #start>
                <RouterLink id="logo-route" :to="homeTarget">
                    <!-- The stek/org identity + navigation live in NavRail now,
                         so this link's own content is normally just the mode/
                         draft/release badges below — except on a hideSidebar
                         page (docs/help), which hides the rail entirely and so
                         has no other visible way back; show a mark there. -->
                    <GotokoLogo v-if="shouldHideSidebar" class="home-mark" />
                    <span v-if="isLiveMode" class="live-badge">LIVE</span>
                    <span v-if="activeRelease" class="release-badge">{{
                        activeRelease
                    }}</span>
                    <span
                        v-if="viewingThreadBranch && !isLiveMode"
                        class="thread-badge"
                        v-tooltip.bottom="`Draft — ${branchTitle}`"
                    >
                        <svg
                            class="thread-badge__icon"
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2.2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            aria-hidden="true"
                        >
                            <circle cx="6" cy="6" r="2.5" />
                            <circle cx="6" cy="18" r="2.5" />
                            <circle cx="18" cy="8" r="2.5" />
                            <path d="M6 8.5v7" />
                            <path d="M18 10.5a6 6 0 0 1-6 6H8.5" />
                        </svg>
                        <span class="thread-badge__label">{{
                            branchTitle
                        }}</span>
                    </span>
                </RouterLink>
            </template>
            <template #end>
                <div class="toolbar-buttons-container">
                    <OmniBox ref="omnibox" />
                    <ActivityBar />
                    <SyncStatus v-if="isModePage || failedSyncs.length" />
                    <ReleaseSelector v-if="stek" />
                    <!-- Build mode renders no diagram, so entity focus and the
                         layer toggles behind this trigger would all be inert —
                         same reason EditViewButton below is hidden there. -->
                    <BusinessViewControls
                        v-if="!isLiveMode && !isBuildMode"
                        :dev="isDevMode"
                    />
                    <EditViewButton
                        v-if="
                            isViewingLatestVersion &&
                            stek &&
                            !isLiveMode &&
                            !isBuildMode &&
                            !activeRelease
                        "
                        ref="editViewButtonRef"
                    />
                    <Button
                        v-if="stek && !isLiveMode && threadsEnabled"
                        type="button"
                        icon="pi pi-comments"
                        severity="secondary"
                        class="threads-toggle"
                        :class="{
                            'threads-toggle--active': threadsStore.drawerOpen,
                        }"
                        v-tooltip.bottom="'Threads'"
                        :aria-pressed="threadsStore.drawerOpen"
                        :aria-label="
                            threadsStore.drawerOpen
                                ? 'Close threads drawer'
                                : 'Open threads drawer'
                        "
                        @click="toggleThreadsDrawer"
                    />
                    <WhatsNewButton />
                </div>
            </template>
        </MenuBar>
    </div>

    <!-- Thread-branch mode: a thin accent line under the top bar reinforces the
         top-bar thread pill + sidebar worktree card so the branch context reads
         at a glance without stealing a full banner row. -->
    <div
        v-if="viewingThreadBranch && !isLiveMode"
        class="thread-accent-strip"
        aria-hidden="true"
    />

    <div
        v-if="activeRelease && !isLiveMode && !viewingThreadBranch"
        class="release-viewing-banner"
    >
        <span class="release-viewing-banner__message">
            <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
            >
                <path
                    fill-rule="evenodd"
                    d="M12 1.5a5.25 5.25 0 0 0-5.25 5.25v3a3 3 0 0 0-3 3v6.75a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3v-6.75a3 3 0 0 0-3-3v-3C17.25 3.85 14.9 1.5 12 1.5zm3.75 8.25v-3a3.75 3.75 0 1 0-7.5 0v3h7.5z"
                    clip-rule="evenodd"
                />
            </svg>
            Viewing release <strong>{{ activeRelease }}</strong> — read-only
        </span>
        <button
            class="release-viewing-banner__action"
            @click="clearActiveRelease()"
        >
            Back to working copy
        </button>
    </div>

    <div
        class="page-with-sidebar"
        :class="{
            'collapsed': ui.showSidebar === false ? true : false,
            'no-sidebar-mode': shouldHideSidebar,
        }"
    >
        <NavRail
            v-if="!shouldHideSidebar"
            :groups="modeGroups"
            :active-mode="activeMode"
            :active-section-key="activeSectionKeyValue"
            @select="onModeSelect"
            @select-landing-mode="onModeLandingSelect"
            @flyout-open="railFlyoutOpen = $event"
        />
        <div
            ref="sidebar"
            id="sidebar"
            :class="{ 'hovering': isHovering }"
            v-if="!shouldHideSidebar && !activeSectionHasNoSubmenu"
        >
            <NavPanelHeader
                :kicker="activeGroup.label"
                :title="activeSection?.label ?? ''"
                :tag="activeSection?.tag"
            >
                <button
                    type="button"
                    class="nav-panel-toggle"
                    title="Hide sidebar"
                    @click="ui.toggleSideBar()"
                >
                    <svg width="18" height="18" fill="none" viewBox="0 0 24 24">
                        <path
                            fill="currentColor"
                            fill-rule="evenodd"
                            d="M10 7h8a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-8V7ZM9 7H6a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h3V7ZM4 8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8Z"
                            clip-rule="evenodd"
                        ></path>
                    </svg>
                </button>
            </NavPanelHeader>
            <NavSideBar />
            <div
                :class="{
                    'resize-handle': true,
                    'resizing': isDragging,
                }"
                @mousedown.prevent="startResize"
            />
        </div>
        <div
            class="main-container"
            :class="{ 'show-history-sidebar': showHistory }"
        >
            <div class="page" :class="{ 'page--full-bleed': isFullBleed }">
                <Transition name="page-enter" mode="out-in">
                    <div :key="pageKey" class="page-view-wrapper">
                        <RouterView />
                    </div>
                </Transition>
            </div>
            <div
                v-if="showHistory"
                class="history-sidebar"
                :style="{
                    width: `${ui.historyWidth}px`,
                    flexBasis: `${ui.historyWidth}px`,
                }"
            >
                <div
                    :class="{
                        'history-resize-handle': true,
                        resizing: isHistoryDragging,
                    }"
                    @mousedown.prevent="startHistoryResize"
                />
                <ArtefactHistory />
            </div>
        </div>

        <ThreadDrawer v-if="stek && !isLiveMode && threadsEnabled" />
        <DomainObjectDrawer v-if="stek && !isLiveMode" />
    </div>

    <HintPopover
        v-if="currentHintStep"
        :current-step="currentHintStep"
        :is-last-step="isLastHintStep"
        @next="nextHintStep"
        @dismiss-all="dismissAllHints"
    />

    <!-- Outside .page-with-sidebar on purpose: the box appears over the page and
         owns no layout space, and nothing out here is mode-gated, so it is
         available in Design, Build and Run alike. -->
    <AskBox />
</template>

<script setup lang="ts">
import { useMouse } from "@vueuse/core";

import Button from "primevue/button";
import {
    computed,
    nextTick,
    onMounted,
    onUnmounted,
    ref,
    useTemplateRef,
    watch,
} from "vue";
import { type RouteRecordName, useRoute, useRouter } from "vue-router";

import GotokoLogo from "@/assets/gotoko-logo-mark.svg?component";
import ActivityBar from "@/components/ActivityBar.vue";
import ArtefactHistory from "@/components/ArtefactHistory.vue";
import AskBox from "@/components/AskBox.vue";
import BusinessViewControls from "@/components/BusinessViewControls.vue";
import EditViewButton from "@/components/EditViewButton.vue";
import NavPanelHeader from "@/components/NavPanelHeader.vue";
import NavRail from "@/components/NavRail.vue";
import NavSideBar from "@/components/NavSideBar.vue";
import OmniBox from "@/components/OmniBox.vue";
import ReleaseSelector from "@/components/ReleaseSelector.vue";
import SyncStatus from "@/components/SyncStatus.vue";
import ThreadDrawer from "@/components/Thread/ThreadDrawer.vue";
import WhatsNewButton from "@/components/WhatsNewButton.vue";
import DomainObjectDrawer from "@/components/entities/DomainObjectDrawer.vue";
import HintPopover from "@/components/hints/HintPopover.vue";
import { clearApplicationFocus } from "@/composables/bpmn-manager/application-focus";
import { clearFocus } from "@/composables/bpmn-manager/entity-focus";
import { useLayerVisibility } from "@/composables/bpmn-manager/layer";
import { useCommandK } from "@/composables/command-k";
import { useEditDomainTermDialog } from "@/composables/edit-domain-term-dialog";
import { useEditEntityDialog } from "@/composables/edit-entity-dialog";
import { useEditRoleDialog } from "@/composables/edit-role-dialog";
import {
    type ExclusiveDrawer,
    useExclusiveRightDrawers,
} from "@/composables/exclusive-drawers";
import { useLiveModeTooltip } from "@/composables/live-mode-tooltip";
import type { Mode, ModeGroupSection } from "@/composables/mode-navigation";
import {
    getModeGroups,
    activeSectionKey as resolveActiveSectionKey,
    resolveModeTarget,
} from "@/composables/mode-navigation";
import { useOrganizationManager } from "@/composables/organization-manager";
import { usePolicyAcceptance } from "@/composables/policy-acceptance";
import { useStekManager } from "@/composables/stek-manager";
import { usePageHints } from "@/composables/use-page-hints";
import { useThreadBranchView } from "@/composables/use-thread-branch-view";
import { pageKeyForRoute } from "@/router/page-key";
import {
    AuthStatus,
    useAuthenticationStore,
} from "@/stores/authentication-store";
import { useDomainObjectDrawerStore } from "@/stores/domain-object-drawer-store";
import { useHistoryManager } from "@/stores/history";
import { useReleaseManager, useReleaseStore } from "@/stores/release-store";
import { useSyncManager } from "@/stores/sync-store";
import { useThreadsStore } from "@/stores/threads-store";
import { useTriageStore } from "@/stores/triage-store";
import { useUIStore } from "@/stores/ui-store";
import { useUserManager } from "@/stores/user-store";
import { debounce } from "@/utils";

const route = useRoute();
const router = useRouter();

// Must match NavRail.vue's own width and the "left: 60px" / "- 60px" offsets
// in this file's .collapsed #sidebar rules below.
const NAV_RAIL_WIDTH = 60;

// Page-wrapper key (drives recreation + the page-enter transition). Element
// pages reuse the page across element->element selection so the BPMN diagram is
// not re-imported on every click — see pageKeyForRoute.
const pageKey = computed(() => pageKeyForRoute(route));
const ui = useUIStore();
const editViewButtonRef = ref<HTMLElement | null>(null);
const {
    currentStep: currentHintStep,
    isLastStep: isLastHintStep,
    nextStep: nextHintStep,
    dismissAll: dismissAllHints,
} = usePageHints(
    [
        {
            id: "edit-view-button",
            targetRef: editViewButtonRef,
            title: "Edit freely, view with confidence",
            content:
                "Use Edit mode to shape your content and View mode to see exactly how others will experience it.",
        },
    ],
    1
);
const { organization } = useOrganizationManager();
const { stek } = useStekManager();
const threadsStore = useThreadsStore();
const domainObjectDrawerStore = useDomainObjectDrawerStore();
const triageStore = useTriageStore();
const layerVisibility = useLayerVisibility();
const { threadsEnabled, buildEnabled, externalCommunicationEnabled } =
    useUserManager();
const authStore = useAuthenticationStore();
const { checkAndShowPolicyDialog } = usePolicyAcceptance();

const { registerHotKey: registerRoleHotKey } = useEditRoleDialog();
const { registerHotKey: registerEntityHotKey } = useEditEntityDialog();
const { registerHotKey: registerTermHotKey } = useEditDomainTermDialog();

registerRoleHotKey();
registerEntityHotKey();
registerTermHotKey();

// Check for policy acceptance when user is authenticated - only on main app pages
watch(
    [() => authStore.authStatus, () => route.name],
    async ([newStatus, routeName]) => {
        if (newStatus === AuthStatus.SIGNED_IN) {
            // Only show policy dialog on main app pages, not on auth/public pages
            const mainAppRoutes: RouteRecordName[] = [
                "home",
                "organizationPage",
                "organizationHome",
                "stekPage",
                "domainPage",
                "productPage",
                "processPage",
                "profile",
                "settings",
                "application",
            ];

            if (routeName && mainAppRoutes.includes(routeName)) {
                // Small delay to ensure user data is fully loaded
                await nextTick();
                setTimeout(async () => {
                    try {
                        await checkAndShowPolicyDialog();
                    } catch (error) {
                        console.error(
                            "Error checking policy acceptance:",
                            error
                        );
                    }
                }, 500);
            }
        }
    },
    { immediate: true }
);

// Note, avoid importing 'showHistory' from the history manager. Somehow
// it will cause the route in the computed artefact in the history manager
// store to be undefined and unreactive.
const showHistory = computed(
    () => "version" in route.params && route.params.version
);

// History isn't a Pinia store like the other two — it's addressed by the
// route's ":version" param — so it's adapted to the same
// {drawerOpen, closeDrawer} shape the other panels already implement.
// closeDrawer drops the param rather than navigating away entirely, matching
// the header's own Close button (ArtefactHistory.vue).
const historyDrawer: ExclusiveDrawer = {
    get drawerOpen() {
        return Boolean(showHistory.value);
    },
    closeDrawer: () => {
        router.push({
            name: route.name,
            params: { ...route.params, version: undefined },
        });
    },
};

// All three right-side panels are kept mutually exclusive: opening any one
// closes the others.
useExclusiveRightDrawers([
    threadsStore,
    domainObjectDrawerStore,
    historyDrawer,
]);

const { isViewingLatestVersion } = useHistoryManager();
const { activeRelease, viewingThreadBranch } = useReleaseManager();
const { clearActiveRelease, setOpenThreadId, clearOpenThreadId } =
    useReleaseStore();

// Title shown in the top-bar thread pill (the sidebar worktree card renders the
// same branch context in fuller form). Exit + the reconcile jump live on the
// sidebar card + the thread drawer, so they're not needed here.
const { branchTitle } = useThreadBranchView();

// Deep-link / reload restore: keep the open-thread state in sync with the
// ?thread= query param, so navigating to (or reloading) a stek URL with a thread
// id shows that thread's branch (worktree at the branch + diff indicators +
// "Viewing thread branch" banner), and dropping the param returns to the working
// copy. Selecting a thread in the drawer (and the entity links, which carry
// ?thread= while a branch is open) sets the param; this makes it survive reloads.
//
// On a *fresh* arrival at a ?thread= URL (deep-link / reload, or a thread change)
// also open the drawer on that thread so the panel reflects the URL — the same
// state you'd be in had you selected the thread from the list. The watcher only
// fires when the param value changes, so navigating between entities on the same
// branch (links carry the unchanged ?thread=) won't re-pop a drawer you closed.
// Gated on threadsEnabled (single source of truth): a deep-linked ?thread= on an org
// without the threads capability must NOT activate the thread branch context (which would
// otherwise send x-thread-revision and try to read a non-existent branch). Reacts to
// threadsEnabled since capabilities load asynchronously via /user/me.
watch(
    [() => route.query.thread, threadsEnabled],
    ([threadId, enabled]) => {
        if (enabled && typeof threadId === "string" && threadId) {
            setOpenThreadId(threadId);
            threadsStore.openDrawer(threadId);
        } else {
            clearOpenThreadId();
        }
    },
    { immediate: true }
);

// Leaving a thread branch (the sidebar card, browser-back, a resolve — all leave
// a thread selected) auto-closes the drawer, so returning to the working copy
// doesn't leave the now-context-less panel pinned open. Exception: the drawer's
// own "back to threads list" clears the selection first, so keep the panel open
// on the list rather than closing it. Only on the true→false transition.
watch(viewingThreadBranch, (onBranch, wasOnBranch) => {
    if (wasOnBranch && !onBranch && threadsStore.drawerThreadId) {
        threadsStore.closeDrawer();
    }
});

// Load the stek's threads as soon as a stek is active AND the org has the threads
// capability, so the entity discussion badges (rendered in the sidebar tree from
// threadsStore.entityThreadMap) populate without needing to open the drawer first.
// Gated on threadsEnabled so non-VFS orgs don't hit the 403'd thread endpoints.
// Also reacts to threadsEnabled because capabilities load asynchronously via /user/me.
watch(
    [() => stek.value?.identity, threadsEnabled],
    ([stekId, enabled]) => {
        if (stekId && enabled) void threadsStore.fetchThreads();
    },
    { immediate: true }
);

// Load the triage issue sources while the Improvements layer is on, so the
// per-entity improvement badges (sidebar tree, page indicators, diagram dots)
// populate without visiting the Triage page first. A separate watcher from
// the threads fetch above so a layer toggle doesn't refire it; the store's
// load() is single-flight, so overlapping with the Triage page's own load
// (same stek-identity trigger) collapses to one request burst.
watch(
    [
        () => stek.value?.identity,
        externalCommunicationEnabled,
        () => layerVisibility.value.triage,
    ],
    ([stekId, enabled, triageLayerOn]) => {
        if (stekId && enabled && triageLayerOn) void triageStore.load();
    },
    { immediate: true }
);

// The top-bar threads icon is now the single open/close affordance (the panel's
// own close icon was removed) and highlights while the drawer is open. While a
// thread branch is being viewed the drawer is pinned open — it's the only "back
// to working copy" path — so a close is vetoed here; exit the branch first via
// the banner or the detail "back" button.
const toggleThreadsDrawer = () => {
    if (threadsStore.drawerOpen) {
        if (viewingThreadBranch.value) return;
        threadsStore.closeDrawer();
    } else {
        threadsStore.openDrawer();
    }
};

// Full-bleed routes (the reconcile page) drop the `.page` --page-margin inset so
// their own chrome (top bar, flush left pane) reaches the content-area edges.
const isFullBleed = computed(() => route.meta.fullBleed === true);

const shouldHideSidebar = computed(() => {
    return route.meta.hideSidebar === true;
});

// Settings-style pages reached via the settings menu (see route meta
// `outsideMode`) aren't part of any of the three modes — the rail still
// renders (so the settings menu in its footer, and the org/stek switcher,
// stay reachable) but nothing in it should look active, and there's no
// submenu panel to show.
const isOutsideMode = computed(() => route.meta.outsideMode === true);

// True only on a page that's actually part of a mode (Design/Build/Run) — not
// a hideSidebar page (docs/help) and not an outsideMode one (Settings,
// Platform Admin, …). The autosave indicator (SyncStatus) reports on a stek's
// sync state, which doesn't exist on either kind of page.
const isModePage = computed(
    () => !shouldHideSidebar.value && !isOutsideMode.value
);
// Sync state is stek-scoped, not page-scoped — a sync kicked off on a mode
// page can still be failing once you've navigated to a settings page, so
// SyncStatus stays visible there too rather than hiding a real failure.
const { failedSyncs } = useSyncManager();

// The top bar is inset to start exactly where .main-container does — after
// the rail, plus the panel's width whenever that slot is actually occupied:
// persisted (expanded), hover-revealed while collapsed (isHovering), or
// showing the rail's hover-peek flyout (railFlyoutOpen) — so it never covers
// the rail or the panel/flyout, only the main content it actually belongs to.
// Overview has no persisted panel of its own to show/hover-reveal, but
// hovering a *different* row still opens that row's own flyout over it, so
// railFlyoutOpen must still grow the inset even there.
const railFlyoutOpen = ref(false);
const navInsetPx = computed(() => {
    if (shouldHideSidebar.value) return "0px";
    const panelOccupiesSlot = activeSectionHasNoSubmenu.value
        ? railFlyoutOpen.value
        : ui.showSidebar || isHovering.value || railFlyoutOpen.value;
    const panelWidth = panelOccupiesSlot ? ui.sidebarWidth : 0;
    return `${NAV_RAIL_WIDTH + panelWidth}px`;
});

const currentMode = computed(() => {
    return route.matched.find((m) => m.meta.mode)?.meta.mode;
});

const isLiveMode = computed(() => currentMode.value === "live");
const isDevMode = computed(() => currentMode.value === "dev");
const isBuildMode = computed(() => currentMode.value === "build");

// Where the top-bar logo/home link goes: the current stek (in whichever mode
// is active), else the current org, else the last stek actually visited (see
// the lastStek watcher below) — the only way back on a hideSidebar page
// (docs/help), which hides the rail (and so NavRail's own org/stek switcher)
// entirely.
const homeTarget = computed(() => {
    if (stek.value && organization.value) {
        const params = {
            orgName: organization.value.name,
            stekName: stek.value.name,
        };
        if (isLiveMode.value) return { name: "livePage", params };
        if (isDevMode.value) return { name: "devPage", params };
        if (isBuildMode.value) return { name: "buildPage", params };
        return { name: "stekPage", params };
    }
    if (organization.value) {
        return {
            name: "organizationPage",
            params: { orgName: organization.value.name },
        };
    }
    if (ui.lastStek) return { name: "stekPage", params: ui.lastStek };
    return "/";
});

// The rail's Design/Build/Run groups. Dev availability is still the stek's own
// setting; Live is gated by subscription entitlement (not a stek setting —
// see useLiveModeTooltip) so its disabled state and tooltip come from there;
// Build is gated by the org's global `build` feature flag (see user-store.ts).
const activeMode = computed<Mode>(
    () => (currentMode.value as Mode) ?? "business"
);
const { liveModeDisabled, liveModeTooltip } = useLiveModeTooltip();
// A settings-style page (route meta `outsideMode`) can land here with no stek
// in context at all (Platform Admin, Activity Metrics, the profile Settings
// pages, Create Stek, Organization Home) — onModeSelect falls back to
// ui.lastStek there (see below), so the rows only need disabling once that
// fallback is also empty (nothing to switch modes on at all yet).
const railModeSwitchDisabled = computed(
    () => !stek.value?.name && !ui.lastStek
);
const modeGroups = computed(() => {
    const groups = getModeGroups({
        devDisabled: stek.value?.dev_mode_enabled === false,
        liveDisabled: liveModeDisabled.value,
        liveDisabledTooltip: liveModeTooltip.value,
        buildEnabled: buildEnabled.value,
    });
    if (!railModeSwitchDisabled.value) return groups;
    return groups.map((group) => ({
        ...group,
        sections: group.sections.map((section) => ({
            ...section,
            disabled: true,
            tooltip: "Open a stek to switch modes",
        })),
    }));
});
// Which row owns the panel/flyout slot — same as activeMode except on a
// Strategy page, where it's "strategy" instead of "business". No row owns it
// on an outsideMode page — it isn't part of any mode, so nothing should look
// active (see activeSectionHasNoSubmenu below).
const activeSectionKeyValue = computed(() =>
    isOutsideMode.value
        ? ""
        : resolveActiveSectionKey(
              activeMode.value,
              route.name as string | undefined
          )
);
const activeGroup = computed(
    () =>
        modeGroups.value.find((g) =>
            g.sections.some((s) => s.modeKey === activeMode.value)
        ) ?? modeGroups.value[0]
);
const activeSection = computed(() =>
    activeGroup.value.sections.find(
        (s) => s.key === activeSectionKeyValue.value
    )
);
// Overview (see ModeGroupSection.noSubmenu) has no tree/worktree of its own,
// and an outsideMode page isn't in any mode to begin with — either way there's
// no persisted #sidebar for it, not just an empty one. The rail itself still
// renders; only the panel next to it is skipped.
const activeSectionHasNoSubmenu = computed(
    () => isOutsideMode.value || activeSection.value?.noSubmenu === true
);

// Falls back to the last stek you were actually on (see the lastStek watcher
// below) when the current page has none of its own — a settings-style page
// (route meta `outsideMode`) — so switching modes from there still goes
// somewhere instead of doing nothing. Only reached as a whole pair: when the
// current org itself resolves (e.g. Organization Home, Create Stek) but its
// stek doesn't, ui.lastStek may belong to a *different* org — pairing that
// org with this one's stek (or vice versa) would silently switch into the
// wrong organization, so this falls back to lastStek only when the org is
// unknown too (mirrors homeTarget above).
const modeSwitchTarget = (): { orgName?: string; stekName?: string } => {
    if (organization.value?.name) {
        return { orgName: organization.value.name, stekName: stek.value?.name };
    }
    return ui.lastStek ?? {};
};

const onModeSelect = (section: ModeGroupSection) => {
    const { orgName, stekName } = modeSwitchTarget();
    if (!orgName || !stekName) return;
    if (section.routeName) {
        router.push({
            name: section.routeName,
            params: { orgName, stekName },
        });
        return;
    }
    router.push(
        resolveModeTarget(
            section.modeKey,
            route.name as string | null | undefined,
            route.params,
            { orgName, stekName }
        )
    );
};

// Picking a group from the rail's "Change mode" popup with no mode active
// (see NavRail's noModeActive/selectLandingMode) — there's no current section
// to preserve context from, so go straight to that mode's landing page.
// Passing a null routeName to resolveModeTarget skips its entity-preserving
// lookup entirely, which is exactly the landing-page fallback we want here.
const onModeLandingSelect = (mode: Mode) => {
    const { orgName, stekName } = modeSwitchTarget();
    if (!orgName || !stekName) return;
    router.push(resolveModeTarget(mode, null, {}, { orgName, stekName }));
};

// Remembers the last stek actually visited, so a settings-style page (route
// meta `outsideMode`, where organization/stek resolve to nothing — see
// onModeSelect and homeTarget) still has somewhere to send you back to.
watch(
    [() => organization.value?.name, () => stek.value?.name],
    ([orgName, stekName]) => {
        if (orgName && stekName) ui.lastStek = { orgName, stekName };
    },
    { immediate: true }
);

// Clear focus when dev mode is disabled, stek switches, or domain context changes.
const clearAllFocus = () => {
    clearFocus();
    clearApplicationFocus();
};
watch(
    () => stek.value?.dev_mode_enabled,
    (enabled) => {
        if (enabled === false) clearAllFocus();
    }
);
watch(
    () => stek.value?.identity,
    (id, prevId) => {
        if (prevId !== undefined && id !== prevId) clearAllFocus();
    }
);
watch(
    () => route.params.domainName,
    (domain, prevDomain) => {
        if (prevDomain !== undefined && domain !== prevDomain) clearAllFocus();
    }
);

// Live mode styling is handled through CSS variables/classes to avoid inconsistencies.
// JS only toggles a single class on <body>.
watch(
    isLiveMode,
    (isLive) => {
        nextTick(() => {
            document.body.classList.toggle("live-mode-active", isLive);
        });
    },
    { immediate: true }
);

// Reflects the active rail group (design/build/run) onto <body> so
// --stekz-primary-color (see _variables.scss) — and anything else styled
// off it — tracks whichever mode the user is currently in, app-wide.
watch(
    () => activeGroup.value.id,
    (groupId) => {
        nextTick(() => {
            document.body.setAttribute("data-mode-group", groupId);
        });
    },
    { immediate: true }
);

// Toggle body class so global CSS can extend padding-top when the release banner is visible.
watch(
    [activeRelease, isLiveMode],
    ([release, live]) => {
        nextTick(() => {
            document.body.classList.toggle(
                "release-viewing-active",
                Boolean(release) && !live
            );
        });
    },
    { immediate: true }
);

const sidebar = useTemplateRef<HTMLElement>("sidebar");

// ⌘K has exactly one registration, and it decides which global surface opens —
// the ask box where it is available, search otherwise. See composables/command-k.ts.
const omnibox = useTemplateRef<{ openSearch: () => void }>("omnibox");
useCommandK(() => omnibox.value?.openSearch());

const isHovering = ref(false);
const isDragging = ref(false);
const { x } = useMouse();

const startResize = () => {
    isDragging.value = true;
    document.addEventListener("mousemove", handleResize);
    document.addEventListener("mouseup", stopResize);
};

const handleResize = (e: MouseEvent) => {
    if (isDragging.value && sidebar.value) {
        const newWidth = e.pageX;
        if (newWidth >= 120 && newWidth <= 500) {
            sidebar.value.style.width = `${newWidth}px`;
            sidebar.value.style.flexBasis = `${newWidth}px`;
            ui.sidebarWidth = newWidth;
        }
    }
};

const stopResize = () => {
    isDragging.value = false;
    document.removeEventListener("mousemove", handleResize);
    document.removeEventListener("mouseup", stopResize);
};

// History panel resize — it's on the right, so its width grows as the mouse
// moves left (width = viewport width minus the cursor's x). Persisted via
// ui.historyWidth; the panel binds it inline, so no imperative style needed.
const isHistoryDragging = ref(false);

const startHistoryResize = () => {
    isHistoryDragging.value = true;
    document.addEventListener("mousemove", handleHistoryResize);
    document.addEventListener("mouseup", stopHistoryResize);
};

const handleHistoryResize = (e: MouseEvent) => {
    if (!isHistoryDragging.value) return;
    const newWidth = window.innerWidth - e.pageX;
    if (newWidth >= 260 && newWidth <= 800) {
        ui.historyWidth = newWidth;
    }
};

const stopHistoryResize = () => {
    isHistoryDragging.value = false;
    document.removeEventListener("mousemove", handleHistoryResize);
    document.removeEventListener("mouseup", stopHistoryResize);
};

watch(x, () => {
    // if the sidebar is collapsed, make it visible (but still collapsed).
    // The permanent 60px rail now sits before the sidebar, so the reveal
    // edge is just past the rail rather than the screen's own edge.
    if (x.value < NAV_RAIL_WIDTH + 5 && !ui.showSidebar) {
        isHovering.value = true;
    }
    // Turn hovering off when mouse x leaves sidebar
    if (
        !isDragging.value &&
        isHovering.value &&
        x.value > NAV_RAIL_WIDTH + ui.sidebarWidth + 10
    ) {
        isHovering.value = false;
    }
});

onMounted(() => {
    updateSidebarWidth();
});

// Cleanup on unmount - no longer needed

// Watch for changes to the shouldHideSidebar value
watch(
    [shouldHideSidebar, sidebar],
    () => {
        if (!shouldHideSidebar.value && sidebar.value) {
            // Use a nextTick to ensure the sidebar element is in the DOM
            nextTick(() => {
                updateSidebarWidth();
            });
        }
    },
    { immediate: true }
);

const updateSidebarWidth = () => {
    if (sidebar.value) {
        sidebar.value.style.width = `${ui.sidebarWidth}px`;
        sidebar.value.style.flexBasis = `${ui.sidebarWidth}px`;
    }
};

// The toolbar buttons wrap onto a second row once the viewport is too narrow
// to fit them next to the start slot (logo/breadcrumb) — see .toolbar-buttons-container's
// flex-wrap. --menu-bar-height then needs to grow to match, since the release
// banner, thread accent strip, thread drawer, and sidebar/main padding are all
// positioned relative to it and would otherwise sit under (or leave a gap above)
// the now-taller menubar.
const menubarRef = ref<{ $el?: HTMLElement }>();
let menubarResizeObserver: ResizeObserver | undefined;
const updateMenuBarHeight = debounce(() => {
    const menubarEl = menubarRef.value?.$el as HTMLElement | undefined;
    if (menubarEl) {
        document.documentElement.style.setProperty(
            "--menu-bar-height",
            `${menubarEl.offsetHeight}px`
        );
    }
}, 100);

onMounted(() => {
    const menubarEl = menubarRef.value?.$el as HTMLElement | undefined;
    if (menubarEl) {
        menubarResizeObserver = new ResizeObserver(updateMenuBarHeight);
        menubarResizeObserver.observe(menubarEl);
    }
});

onUnmounted(() => {
    menubarResizeObserver?.disconnect();
});
</script>

<style scoped lang="scss">
.menubar-wrapper {
    position: fixed;
    width: 100%;
    z-index: 1000;
    top: 0;
    left: 0;
}

.release-viewing-banner {
    position: fixed;
    top: var(--menu-bar-height);
    left: 0;
    width: 100%;
    z-index: 999;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    height: var(--release-banner-height, 36px);
    padding: 0 1rem;
    background: #fef3c7;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);
    color: #78350f;
    font-size: 0.85rem;

    &__message {
        display: flex;
        align-items: center;
        gap: 0.4rem;
    }

    &__action {
        background: none;
        border: 1px solid #b45309;
        color: #78350f;
        cursor: pointer;
        font-size: 0.8rem;
        padding: 0.15em 0.6em;

        &:hover {
            background: #fde68a;
        }
    }
}

// Thread-branch mode: a 3px accent line pinned directly under the top bar. It
// reinforces the top-bar thread pill + sidebar worktree card as an ambient,
// full-width "you're on a branch" cue without reflowing the page like a banner.
.thread-accent-strip {
    position: fixed;
    top: var(--menu-bar-height);
    left: 0;
    width: 100%;
    height: var(--thread-accent-strip-height);
    z-index: 999;
    background: var(--stekz-build-color);
}

.p-menubar {
    position: fixed;
    // --nav-inset (set on .menubar-wrapper, inherited via normal CSS
    // cascade regardless of the component boundary) is the rail's width plus
    // the panel's current width when it's actually reserving that space —
    // so the top bar starts exactly where .main-container does and never
    // covers the rail or the panel/flyout.
    left: var(--nav-inset, 0px);
    width: calc(100% - var(--nav-inset, 0px));
    z-index: 1000;
    // Once .p-menubar-start (flex-shrink: 0 below) and .p-menubar-end no longer
    // fit on one line, wrap the whole end slot onto its own row below start
    // instead of squeezing everything onto one line. .p-menubar-end's own
    // margin-left: auto (PrimeVue's base theme) still right-aligns it once it's
    // alone on that second row.
    flex-wrap: wrap;

    :deep(.p-menubar-start) {
        overflow-x: auto;
        // PrimeVue's own responsive breakpoint (~960px, independent of this app's
        // 768px convention) adds a `p-menubar-mobile` class that changes the
        // menubar's internal flex distribution. That can squeeze this slot's
        // flex-basis all the way to 0 to make room for other menubar content,
        // taking the sidebar toggle button down with it (not a visibility/opacity
        // issue — the button's own box shrinks to zero width). Force the slot to
        // never shrink below the width its content actually needs so the toggle
        // button can't be squeezed away purely by viewport width.
        flex-shrink: 0;
        min-width: max-content;
    }

    :deep(.p-menubar-end) {
        display: flex;
        gap: 0.5em;
    }
    // min-height (not height), and off --menu-bar-base-height rather than
    // --menu-bar-height, so the bar can grow to a second row once the toolbar
    // buttons wrap and shrink back once they don't — see updateMenuBarHeight().
    min-height: var(--menu-bar-base-height);
}

// The threads toggle takes on the primary tint while the drawer is open — the
// same active-state treatment the Edit/View toggle uses — so the icon reads as
// "active" now that the panel's own close affordance is gone.
.threads-toggle.threads-toggle--active {
    background-color: var(--stekz-primary-background);
    border-color: var(--stekz-primary-background);
    color: var(--stekz-primary-color);
}

// Keep header text styling the same in design & live; only the LIVE badge differs.

.nav-panel-toggle {
    width: 26px;
    height: 26px;
    flex: 0 0 auto;
    display: grid;
    place-items: center;
    border: none;
    background: none;
    color: var(--text-color-secondary);
    cursor: pointer;
    transition:
        color 0.14s,
        background 0.14s;

    &:hover {
        color: var(--text-color);
        background: var(--surface-background-mild);
    }
}
</style>

<style lang="scss">
:root {
    --release-banner-height: 36px;
    --thread-accent-strip-height: 3px;
}

body.release-viewing-active {
    .page-with-sidebar {
        #sidebar,
        .main-container {
            padding-top: calc(
                var(--menu-bar-height) + var(--release-banner-height)
            );
        }
    }
}

// Global styles for live mode - override PrimeVue CSS variables
// NOTE: Avoid global "force white" overrides here.
// They made the stek title/logo invisible when the menubar background wasn't actually in live mode.

// (Removed) live-mode gradient + forced colors to keep header consistent with design mode.

a#logo-route {
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
    align-items: center;
    justify-content: center;
    padding-left: 8px;
    padding-right: 8px;
    text-decoration: none;
    text-decoration: inherit;
    visibility: visible !important;
    opacity: 1 !important;

    .home-mark {
        width: 22px;
        height: 22px;
        color: var(--text-color);
    }

    @keyframes live-pulse {
        0%,
        100% {
            opacity: 1;
            transform: scale(1);
        }
        50% {
            opacity: 0.3;
            transform: scale(0.75);
        }
    }

    .live-badge {
        display: inline-flex;
        align-items: center;
        gap: 0.35em;
        font-size: 0.6em;
        font-weight: 700;
        padding: 0.2em 0.5em;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: #d32f2f; /* red */
        border: 1px solid #d32f2f;
        background: transparent;

        &::before {
            content: "";
            display: block;
            width: 0.55em;
            height: 0.55em;
            border-radius: 50%;
            background-color: #d32f2f;
            animation: live-pulse 1.4s ease-in-out infinite;
            flex-shrink: 0;
        }
    }

    .release-badge {
        font-size: 0.6em;
        font-weight: 700;
        padding: 0.2em 0.5em;
        letter-spacing: 0.05em;
        color: var(--stekz-primary-color, #1664d9);
        border: 1px solid var(--stekz-primary-color, #1664d9);
        background: transparent;
    }

    // Thread-branch pill — the always-visible top-bar cue that a thread's branch
    // is being viewed. Pink (secondary) keeps it distinct from the purple release
    // badge and the red LIVE badge. The title truncates so a long thread title
    // never blows out the header; the full title is in the tooltip + sidebar card.
    .thread-badge {
        display: inline-flex;
        align-items: center;
        gap: 0.35em;
        max-width: 16em;
        font-size: 0.6em;
        font-weight: 700;
        padding: 0.2em 0.5em;
        letter-spacing: 0.05em;
        color: var(--stekz-build-color, #f28c28);
        border: 1px solid var(--stekz-build-color, #f28c28);
        background: transparent;

        &__icon {
            flex: 0 0 auto;
        }

        &__label {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }
}

// Keep logo/title colors consistent across modes; LIVE badge is handled above.

.page-with-sidebar {
    display: flex;
    flex-direction: row;

    // Make room for the fixed top bar. The rail and #sidebar are excluded —
    // --nav-inset now grows to cover both of them whenever that slot is
    // actually occupied (persisted, hover-revealed, or the rail's flyout),
    // so the top bar doesn't cover either and neither needs a top offset of
    // its own. Only .main-container, which the top bar always does sit
    // above, keeps one.
    .main-container {
        padding-top: var(--menu-bar-height);
        position: relative;
    }

    // The rail is never part of the collapse/resize column — it always stays
    // in the flex flow at a fixed 60px, so mode-switching remains reachable
    // even while the tree panel is collapsed (NavRail.vue sizes its own
    // height; nothing else here needs to size it).

    &.collapsed {
        #sidebar {
            z-index: 600;
            position: fixed;
            // Sit to the right of the permanent rail instead of flush left,
            // so the hover-revealed panel doesn't cover the rail.
            left: 60px;

            // Hidden state must clear both its own width AND the rail's
            // 60px offset above, or a sliver of it keeps bleeding back into
            // view right on top of the rail.
            &:not(.hovering) {
                transform: translateX(calc(-100% - 60px));
            }
        }
    }

    &.no-sidebar-mode {
        .main-container {
            padding-left: 0;
            margin-left: 0;
        }
    }

    #sidebar {
        height: 100vh;
        background: var(--p-menubar-background);
        border-right: 2px solid var(--surface-border, #dee2e6);
        display: flex;
        flex-direction: column;
        flex-grow: 0;
        flex-shrink: 0;
        left: 0;
        min-width: 120px;
        max-width: 500px;
        overflow-y: hidden; // note we need this for the default resize handle
        position: relative; /* Needed for absolute positioning of handle */
        transform: translateX(0);
        transition-property: transform;
        transition-timing-function: ease;
        transition-duration: 200ms;

        &.on-top {
            z-index: 600;
            overflow: visible;
        }
    }

    .main-container {
        width: 100%;
        min-width: 0;
        height: 100vh;
        overflow-y: scroll;
        flex-grow: 1;
        display: flex;
        flex-direction: column;

        &.show-history-sidebar {
            flex-direction: row;

            // The nav sidebar (up to 500px) and History (up to 800px) resize
            // independently, so both dragged to their max on a <=1440px
            // screen can otherwise squeeze .page down to its padding alone.
            // Past this floor, .main-container's own overflow-x takes over
            // instead of the content becoming unreadable.
            > .page {
                min-width: 320px;
            }
        }

        .history-sidebar {
            // .main-container is the scroll container and becomes a flex row when
            // history is open, so as a normal child the panel scrolled away with
            // the (much taller) page content. Pin it. Sticky insets and height:100%
            // both resolve against .main-container's content box, which its own
            // padding-top (menubar, plus the release banner via
            // body.release-viewing-active) has already pushed below the bar — so
            // top:0 + height:100% pin it flush under the bar and fill the viewport
            // without re-adding those offsets (adding them double-counts, and
            // --release-banner-height is a non-zeroed 36px constant), tracking the
            // banner automatically. Its own .container then scrolls internally.
            position: sticky;
            top: 0;
            align-self: flex-start;
            height: 100%;
            display: flex;
            flex: 0 0 auto;
            border-left: 1px solid var(--palette-grey-300);
            flex-direction: column;
            min-width: 260px;
            max-width: 800px;
        }
    }
}

.page {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    padding: calc(var(--page-margin) * 0.5) var(--page-margin);
    background: var(--p-content-background);
}

/* Full-bleed routes opt out of the page inset (see route meta `fullBleed`) so a
   page with its own top bar + edge-anchored panes fills the content area. */
.page.page--full-bleed {
    padding: 0;
}

.resize-handle {
    position: absolute;
    top: 0;
    right: 0;
    width: 3px;
    height: 100%;
    background: transparent;
    cursor: col-resize;
    transition: 100ms 50ms ease-in-out;

    &:hover,
    &.resizing {
        background: var(--stekz-primary-background);
    }
}

// History panel handle sits on the panel's LEFT edge (the panel is on the right).
.history-resize-handle {
    position: absolute;
    top: 0;
    left: 0;
    width: 3px;
    height: 100%;
    background: transparent;
    cursor: col-resize;
    z-index: 1;
    transition: 100ms 50ms ease-in-out;

    &:hover,
    &.resizing {
        background: var(--stekz-primary-background);
    }
}

.popover-container {
    .info-text {
        padding: 1rem;
        text-align: center;
    }

    :deep(.p-datatable) {
        .sync-action-buttons {
            display: flex;
            gap: 8px;
        }
    }
}

.toolbar-buttons-container {
    display: flex;
    gap: 0.5em;
}

/* Wrapper that holds RouterView inside the page transition — must fill available space */
.page-view-wrapper {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
    min-width: 0;
}

/* Page enter transition — subtle fade + slide-up on every route change */
.page-enter-enter-active {
    transition:
        opacity 160ms ease-out,
        transform 160ms ease-out;
}
.page-enter-leave-active {
    transition: opacity 80ms ease-in;
}
.page-enter-enter-from {
    opacity: 0;
    transform: translateY(6px);
}
.page-enter-leave-to {
    opacity: 0;
}
</style>
